import { track } from '../lib/funnel';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Hand, ALargeSmall, ArrowLeftRight, Check, Lightbulb, Eye, Footprints, Globe, GraduationCap } from 'lucide-react';
import { COUNTRIES, getCountry, setCountry, len, depth, press, money, locale } from '../lib/units';
import { DepthReqToggle } from './DepthPrompt';
import { RtkAutoToggle } from './RtkAuto';

const regionName = (c, loc) => { try { return new Intl.DisplayNames([loc], { type: 'region' }).of(c); } catch { return c; } };
import { WalkToggle } from './MapUi';
import { getA11y, setA11y, speak } from '../lib/a11y';
import { useGlove, setGlove } from '../lib/glove';
import { getScale, applyScale } from '../lib/uiScale';
import { setTourHelp } from './TourGuide';

// first-start / Starthilfe: explains and sets hand side, view size and glove mode
const KEY = 'giss_setup_done';
const THEN = 'giss_setup_then_profiles';
// pilot test: every device (also ones that opened the web app earlier) sees the control setup once more
if (localStorage.getItem('giss_setup_v') !== '2') { localStorage.removeItem(KEY); localStorage.setItem('giss_setup_v', '2'); }
// Starthilfe: setup first, then sector choice, then tours
export const resetSetup = () => { localStorage.removeItem(KEY); sessionStorage.setItem(THEN, '1'); window.dispatchEvent(new Event('giss-setup')); };
const ready = () => !localStorage.getItem(KEY) && !document.querySelector('.editor-backdrop, .gnss-backdrop, .prec-backdrop, .ft-overlay, .stc-overlay');

const Opt = ({ on, onClick, testid, children }) => (
  <button type="button" className={`su-opt ${on ? 'on' : ''}`} data-testid={testid} aria-pressed={on} onClick={onClick}>{on && <Check size={16} className="su-tick" />}{children}</button>
);

export default function SetupGuide({ t }) {
  const [open, setOpen] = useState(false);
  const [sc, setSc] = useState(getScale);
  const [a, setA] = useState(getA11y);
  const [country, setCtry] = useState(getCountry);
  const g = useGlove();
  const [help, setHelp] = useState('quiet');
  useEffect(() => { if (open) track('setup_shown'); }, [open]);
  useEffect(() => {
    const id = setInterval(() => { if (ready()) setOpen(true); }, 900);
    return () => clearInterval(id);
  }, []);
  if (!open) return null;
  const size = (i) => { applyScale(i); setSc(i); };
  const done = () => {
    localStorage.setItem(KEY, '1'); setOpen(false); track('setup_done');
    setTourHelp(help);
    sessionStorage.removeItem(THEN);
    window.dispatchEvent(new Event('giss-setup-done'));
  };
  return createPortal(
    <div className="editor-backdrop su-backdrop" data-testid="setup-guide">
      <div className="editor su-card">
        <h2 className="su-title">{t.suTitle}</h2>
        <p className="muted">{t.suIntro}</p>
        <h3><Globe size={18} /> {t.unTitle}</h3>
        <label className="su-country">
          <select data-testid="setup-country" value={country} onChange={(e) => { setCountry(e.target.value); setCtry(e.target.value); }}>
            {Object.keys(COUNTRIES).map((c) => <option key={c} value={c}>{regionName(c, t.locale)}</option>)}
          </select>
          <small className="muted" data-testid="setup-units-preview">{len(12.5)} · {depth(1.2)} · {press(4)} · {money(1234.5)} · {new Date().toLocaleDateString(locale())}</small>
        </label>
        <h3><ArrowLeftRight size={18} /> {t.suHand}</h3>
        <div className="su-row">
          <Opt on={g.hand === 'l'} testid="setup-hand-left" onClick={() => setGlove({ hand: 'l' })}><b>{t.suHandL}</b><small>{t.suHandLd}</small></Opt>
          <Opt on={g.hand === 'r'} testid="setup-hand-right" onClick={() => setGlove({ hand: 'r' })}><b>{t.suHandR}</b><small>{t.suHandRd}</small></Opt>
        </div>
        <h3><GraduationCap size={18} /> {t.suHelp}</h3>
        <div className="su-row su-3" data-testid="setup-help">
          {[['voice', t.suHelpVoice, t.suHelpVoiceD], ['quiet', t.suHelpQuiet, t.suHelpQuietD], ['no', t.suHelpNo, t.suHelpNoD]].map(([k, n, d]) => (
            <Opt key={k} on={help === k} testid={`setup-help-${k}`} onClick={() => { setHelp(k); if (k === 'voice') { setA11y({ voice: true }); setA({ ...a, voice: true }); speak(t.suHelpVoice, true); } }}><b>{n}</b><small>{d}</small></Opt>
          ))}
        </div>
        <h3><ALargeSmall size={18} /> {t.uiView}</h3>
        <div className="su-row su-3">
          {[t.uiN, t.uiL, t.uiXL].map((n, i) => <Opt key={n} on={sc === i} testid={`setup-size-${i}`} onClick={() => size(i)}><b style={{ fontSize: 14 + i * 3 }}>{n}</b></Opt>)}
        </div>
        <h3><Hand size={18} /> {t.glBtn}</h3>
        <Opt on={g.on} testid="setup-glove" onClick={() => { setGlove({ on: !g.on }); if (!g.on) navigator.vibrate?.(60); }}><b>{g.on ? t.glOn : t.glOff}</b><small>{t.suGloveD}</small></Opt>
        <h3><Eye size={18} /> {t.a11yTitle}</h3>
        <div className="su-row">
          <Opt on={a.cb} testid="setup-colorblind" onClick={() => { setA11y({ cb: !a.cb }); setA({ ...a, cb: !a.cb }); }}><b>{t.a11yCb}</b><small>{t.a11yCbD}</small></Opt>
          <Opt on={a.voice} testid="setup-voice" onClick={() => { setA11y({ voice: !a.voice }); setA({ ...a, voice: !a.voice }); if (!a.voice) speak(t.a11yVoiceHello, true); else if (help === 'voice') setHelp('quiet'); }}><b>{t.a11yVoice}</b><small>{t.a11yVoiceD}</small></Opt>
        </div>
        <h3><Footprints size={18} /> {t.walkHide}</h3>
        <WalkToggle t={t} testid="setup-walk-hide" />
        <DepthReqToggle t={t} testid="setup-depth-req" />
        <RtkAutoToggle t={t} testid="setup-rtk-auto" />
        <h3><Lightbulb size={18} /> {t.suTips}</h3>
        <ul className="su-tips" data-testid="setup-tips">{t.suTipList.map((x) => <li key={x}>{x}</li>)}</ul>
        <button className="btn btn-primary su-go" data-testid="setup-done-btn" onClick={done}>{t.suGo}</button>
      </div>
    </div>,
    document.body,
  );
}
